import React, {useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {useForm} from "antd/lib/form/Form";
import {useDispatch, useSelector} from "react-redux";

import EditDeviceForm from "./components/EditDeviceForm";
import {devicesAC} from "../../store/branches/devices/actionCreators";
import {Device} from "../../store/branches/devices/stateTypes";
import {sitesAC} from "../../store/branches/sites/actionCreators";
import DeviceAlert from "../Alerts/device";
import {usePermissions} from "../../hooks/usePermissions";
import {LoadingStatus} from "../../store/status";
import Preloader from "../Preloader";
import {selectDevicesState, selectSitesState} from "../../store/selectors";
import {getKeyByValue} from "../../helpers/getKeyByValue";


const EditDevice: React.FC = () => {
    const [form] = useForm();
    const dispatch = useDispatch();
    const history = useHistory();
    const {id}: any = useParams();

    const [device, setDevice] = useState<Device>(null);
    const [deviceType, setDeviceType] = useState<string>("");
    const [selectedLocationId, setSelected] = useState<number>(null);

    const {isSuperUser, isOEM, hasAccessToEdit} = usePermissions();

    const {status, current_device} = useSelector(selectDevicesState);
    const {all_devices, deviceSubTypes} = useSelector(selectDevicesState);
    const {sitesData} = useSelector(selectSitesState);
    const locations = sitesData?.results;

    const onSubmit = (values: any) => {
        const data = {
            id: device.id,
            location: selectedLocationId,
            title: values.title,
            serial_number: values.serial_number,
            code: values.code,
            device_type: deviceType,
            ip_address: values.ip_address,
            udf_id: values.udf_id,
            // antd's Select(allowClear) leaves a cleared field as `undefined`,
            // not `null` — and JSON.stringify drops `undefined` keys entirely,
            // so the backend never even sees the field was cleared. Coerce to
            // an explicit null so the PATCH body always carries it.
            device_sub_type_id: values.device_sub_type_id ?? null,
        };
        dispatch(devicesAC.updateCurrentDevice(data));
        form.resetFields();
    };

    const DEVICE_TYPE = {
        "F500": "F500",
        "0300": "WDC4",
        "0005": "ETH-NODE-1",
        "0006": "ETH-NODE-2",
        "F500-UDF": "F500 UDF",
        "0007": "ETH-NODE-3",
        "0008": "BARRIER",
        "00TX": "BARRIER-TX",
        "001W": "1-WIRE",
        "0009": "WDC-LITE",
    };

    const handleSelectDeviceType = (value: any, option: any) => {
        const key = getKeyByValue(DEVICE_TYPE, option.key);
        setDeviceType(key);
    };

    const handSelectLocation = (value: any, option: any) => {
        setSelected(Number(option.key));
    };

    const onCancel = () => {
        form.resetFields();
        history.push("/devices");
    };

    useEffect(() => {
        if (current_device) {
            setDevice(current_device);
            setDeviceType(current_device.device_type);
        }

        return () => {
            setDevice(null);
            form.resetFields();
        };
    }, [form, current_device, setDevice, device]);


    useEffect(() => {
        if (device) {
            setSelected(device.location);
        }
    }, [device]);

    useEffect(() => {
        if (!locations) {
            dispatch(sitesAC.fetchSites());
        }
    }, [dispatch, locations]);


    useEffect(() => {
        if (!current_device) {
            dispatch(devicesAC.fetchCurrentDevice(id));
        }
    }, [dispatch, id, current_device]);

    useEffect(() => {
        dispatch(devicesAC.fetchAllDevices());
    }, [dispatch]);

    useEffect(() => {
        dispatch(devicesAC.fetchDeviceSubTypes());
    }, [dispatch]);

    const onDeactivateDevice = () => {
        dispatch(devicesAC.deactivateDevice(id));
    };

    const onRemoveDevice = () => {
        dispatch(devicesAC.removeDevice(id));
    };

    if (!hasAccessToEdit) {
        history.push("/devices");
    }

    return (
        <Preloader isLoaded={status === LoadingStatus.LOADING}>
            <React.Fragment>
                <DeviceAlert/>
                <EditDeviceForm form={form}
                                locations={locations}
                                device={device}
                                isSuperUser={isSuperUser}
                                isOEM={isOEM}
                                device_type={DEVICE_TYPE}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                handSelectLocation={handSelectLocation}
                                handleSelectDeviceType={handleSelectDeviceType}
                                onDeactivateDevice={onDeactivateDevice}
                                onRemoveDevice={onRemoveDevice}
                                currentDeviceType={deviceType}
                                deviceSubTypes={deviceSubTypes}
                />
            </React.Fragment>
        </Preloader>
    );
};
export default EditDevice;
